import { cardPeriodForDate } from "./finance";
import { pendingBreakdown } from "./pending";
import type { FinancialSnapshot, Movement } from "./types";
import { isExpectedInstallment } from "./bank-posting";

export interface CardReserveConfig {
  paymentMethod: string;
  cutDay: number;
}

export interface CardReserveSummary {
  name: string;
  reservedCents: number;
  unassignedCents: number;
  period?: string;
  movementCount: number;
  grossCents: number;
  registeredCents: number;
  expectedCents: number;
  coveredCents: number;
  ownPendingCents: number;
  reimbursementPendingCents: number;
  pendingCents: number;
  coveragePercent: number;
}

/**
 * Construye únicamente una vista del corte vigente. El saldo reservado sigue
 * viniendo de la fotografía financiera y no se recalcula desde movimientos.
 */
export function cardReserveSummaries(
  snapshot: Pick<FinancialSnapshot, "cardPayments">,
  movements: Movement[],
  configs: CardReserveConfig[],
  referenceDate: string,
): CardReserveSummary[] {
  return snapshot.cardPayments.map((card) => {
    const config = configs.find((candidate) => candidate.paymentMethod === card.name);
    const period = cardPeriodForDate(referenceDate, config?.cutDay ?? 16);
    const currentMovements = movements.filter((movement) => {
      if (movement.kind !== "expense" || movement.paymentMethod !== card.name) return false;
      const movementDate = String(movement.spentOn ?? movement.createdAt).slice(0, 10);
      const movementPeriod = movement.cardPeriod ?? cardPeriodForDate(movementDate, config?.cutDay ?? 16);
      return movementPeriod === period;
    });
    const grossCents = currentMovements.reduce(
      (sum, movement) => sum + Math.max(0, movement.billedAmountCents ?? movement.amountCents),
      0,
    );
    const coveredCents = currentMovements.reduce(
      (sum, movement) => sum + Math.max(0, movement.coveredAmountCents ?? movement.amountCents),
      0,
    );
    const expectedCents = currentMovements.filter(movement => isExpectedInstallment(movement, referenceDate))
      .reduce((sum, movement) => sum + Math.max(0, movement.billedAmountCents ?? movement.amountCents), 0);
    const pending = currentMovements.reduce((total, movement) => {
      const breakdown = pendingBreakdown(movement);
      total.own += breakdown.ownPendingCents;
      total.reimbursement += breakdown.reimbursementPendingCents;
      return total;
    }, { own: 0, reimbursement: 0 });

    return {
      name: card.name,
      reservedCents: card.amountCents,
      unassignedCents: Math.max(0, card.unassignedCents ?? 0),
      period,
      movementCount: currentMovements.length,
      grossCents,
      registeredCents: grossCents - expectedCents,
      expectedCents,
      coveredCents,
      ownPendingCents: pending.own,
      reimbursementPendingCents: pending.reimbursement,
      pendingCents: pending.own + pending.reimbursement,
      coveragePercent: grossCents ? Math.min(100, Math.max(0, coveredCents / grossCents * 100)) : 0,
    };
  });
}
